import { useEffect, useState } from "react";
import { ArrowLeft, Clock } from "lucide-react";
import CaseStudyVideo from "../components/content/CaseStudyVideo";
import ContentBlocks from "../components/content/ContentBlocks";
import {
  ArticleCard,
  postDate,
  readingTime,
} from "../components/sections/Cards";
import CtaPanel from "../components/sections/CtaPanel";
import PageHero from "../components/sections/PageHero";
import { ButtonLink } from "../components/ui/Button";
import SectionHeading from "../components/ui/SectionHeading";
import TextLink from "../components/ui/TextLink";
import { insights, pageTitle } from "../content";
import { routes } from "../data/site";
import { blocksBefore, headingId, imageKey } from "../lib/content-blocks";
import { base, imageProps, localHref, pathnameOf } from "../lib/links";
import { isYouTubeUrl } from "../lib/youtube";
import { cx } from "../lib/cx";

// WordPress trailers captured after the post: comment form, recent posts and
// related links. The page renders its own "Keep reading" cards instead.
const trailingSections =
  /Leave a Reply|Recent Posts|Related Posts|RELATED|The True Cost of Generic/;

/** Leftover theme images (the site favicon) that aren't part of the post. */
const isThemeImage = (block) =>
  block.type === "image" && /favicon/i.test(block.src);

/**
 * Most captured posts saved their section headings one level down (h3, with
 * h4 beneath), so they rendered as small body headings while other posts got
 * full section headings. Lift such posts a level so every post matches.
 */
function liftHeadings(blocks, title) {
  const isSection = (b) => b.type === "h2" && b.text !== title;
  if (blocks.some(isSection) || !blocks.some((b) => b.type === "h3"))
    return blocks;
  const up = { h3: "h2", h4: "h3", h5: "h4" };
  return blocks.map((b) => (up[b.type] ? { ...b, type: up[b.type] } : b));
}

/**
 * The post's sections, for the table of contents: its h2s, or its h3s when
 * the post is organised by h3 alone.
 */
function sectionsOf(blocks, title) {
  const at = (level) =>
    blocks
      .map((block, index) => ({ ...block, index }))
      .filter(
        (b) => b.type === level && b.text !== title && !/^\d+$/.test(b.text),
      )
      .map((b) => ({ id: headingId(b.text, b.index), text: b.text }));
  const h2s = at("h2");
  return h2s.length > 1 ? h2s : at("h3");
}

/** "On this page", highlighting the section being read. */
function TableOfContents({ sections }) {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const headings = sections
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);
    if (!headings.length || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -65% 0px" },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <nav aria-label="On this page" className="hidden lg:block">
      <h2 className="eyebrow mb-4 text-blue-deep">On this page</h2>
      <ol className="border-l-2 border-line text-[15px] leading-snug">
        {sections.map(({ id, text }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              aria-current={active === id ? "location" : undefined}
              className={cx(
                "-ml-0.5 block border-l-2 py-2 pl-4 transition-colors",
                active === id
                  ? "border-yellow font-bold text-ink"
                  : "border-transparent text-muted hover:border-line-strong hover:text-ink",
              )}
            >
              {text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function AsideCta() {
  return (
    <div className="blueprint rounded-brand p-6 text-white shadow-lift">
      <span className="eyebrow text-blue-soft">Make building easier</span>
      <p className="font-display mt-3 mb-2 text-[26px] leading-[1.1] uppercase">
        Sound like your <span className="text-yellow">office?</span>
      </p>
      <p className="mb-5 text-[15px] text-white/80">
        One meeting. You talk, we listen, and you leave with a plain map of what
        to fix first.
      </p>
      <ButtonLink size="md" className="w-full" />
    </div>
  );
}

/** The three posts after this one in the blog's order, wrapping around. */
function nextPosts(path, count = 3) {
  const i = insights.findIndex((card) => pathnameOf(card.href) === path);
  const others = [...insights.slice(i + 1), ...insights.slice(0, i)];
  return (i === -1 ? insights : others).slice(0, count);
}

/** A blog post: captured prose with a table of contents and a CTA sidebar. */
export default function ArticlePage({ page, path }) {
  const title = pageTitle(page);
  const cover = page.image && localHref(page.image);
  const coverKey = page.image && imageKey(page.image);
  const blocks = liftHeadings(
    blocksBefore(page.body || [], trailingSections).filter(
      (block, i) =>
        !isThemeImage(block) &&
        // The hero shows the cover photo and the title.
        !(block.type === "image" && imageKey(block.src) === coverKey) &&
        !(i < 2 && block.type === "h2" && block.text === title),
    ),
    title,
  );
  const sections = sectionsOf(blocks, title);
  const videos = page.externalEmbeds?.filter(isYouTubeUrl) || [];
  const time = readingTime(page);
  const related = nextPosts(path);
  // A video post's player is its picture; don't show its still twice.
  const heroImage =
    !videos.length && cover?.startsWith(base) ? cover : undefined;

  return (
    <>
      <PageHero
        eyebrow={videos.length ? "Blog · Video" : "Blog"}
        title={title}
        image={heroImage}
        long={title.length > 40}
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[15px]">
          <a
            href={localHref(routes.insights)}
            className="inline-flex items-center gap-2 font-bold text-white/85 transition-colors hover:text-yellow"
          >
            <ArrowLeft size={17} aria-hidden="true" />
            All posts
          </a>
          {page.date && (
            <time dateTime={page.date} className="text-white/70">
              {postDate(page.date)}
              {page.author && ` · ${page.author}`}
            </time>
          )}
          {time && (
            <span className="flex items-center gap-2 text-white/70">
              <Clock size={16} aria-hidden="true" />
              {time}
            </span>
          )}
        </div>
      </PageHero>

      <section className="bg-white px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto grid max-w-280 grid-cols-1 items-start gap-14 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-20">
          <article className="min-w-0 max-w-[70ch]">
            {/* The hero hides its photo below lg; show the cover here instead. */}
            {heroImage && (
              <img
                {...imageProps(heroImage, "(max-width: 1024px) 100vw, 1px")}
                alt=""
                className="mb-10 aspect-video w-full rounded-brand object-cover shadow-soft lg:hidden"
              />
            )}
            {videos.map((url) => (
              <div key={url} className="mb-10">
                <CaseStudyVideo url={url} title={`${title} video`} />
              </div>
            ))}
            <ContentBlocks blocks={blocks} title={title} />
            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-8">
              <a
                href={localHref(routes.insights)}
                className="inline-flex items-center gap-2 font-bold text-blue-deep transition-colors hover:text-ink"
              >
                <ArrowLeft size={17} aria-hidden="true" />
                Back to the blog
              </a>
              <ButtonLink size="md" />
            </div>
          </article>

          <aside className="flex flex-col gap-10 lg:sticky lg:top-28">
            {sections.length > 1 && <TableOfContents sections={sections} />}
            <AsideCta />
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-surface px-4 py-20 sm:px-6 md:py-24">
          <div className="mx-auto max-w-280">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
              <SectionHeading eyebrow="From the blog" title="Keep *reading.*" />
              <TextLink
                href={routes.insights}
                className="text-blue-deep hover:text-ink"
              >
                All posts
              </TextLink>
            </div>
            <ul className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((card) => (
                <li key={card.href} className="grid">
                  <ArticleCard card={card} />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <CtaPanel className="bg-white" />
    </>
  );
}
